import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { FeatureFlagApplicationKey } from '@gandalf/constants';
import { FEATURE_FLAGS } from '@core/feature/feature.constants';
import { FeatureService } from '@core/feature/feature.service';
import { LegacyNavigationService } from '@core/legacy/legacy-navigation.service';
import { SecurityManagerService } from '@core/security-manager/security-manager.service';
import { APP_ROUTING_CONSTANTS } from '../../app-routing.constants';

export const canActivateLegacyGuard: CanActivateFn = async (
	next: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
): Promise<boolean> => {
	const featureService = inject(FeatureService);
	const router = inject(Router);
	const legacyNavigationService = inject(LegacyNavigationService);
	const securityManagerService = inject(SecurityManagerService);
	const pathKey = next.data.key;
	const legacyPath = 'legacy';

	let primaryContext;
	let subContext = [];
	let hideLegacy = true;
	switch (pathKey) {
		case APP_ROUTING_CONSTANTS.ACCOUNTING.INVOICES:
		case APP_ROUTING_CONSTANTS.ACCOUNTING.CLAIMS:
		case APP_ROUTING_CONSTANTS.ACCOUNTING.PAYMENTS:
			primaryContext = 'accounting';
			break;
		case APP_ROUTING_CONSTANTS.CONNECT.DASHBOARD:
			primaryContext = 'connect';
			subContext = ['connectDashboard'];
			hideLegacy = true;
			break;
		case APP_ROUTING_CONSTANTS.CONNECT.CAMPAIGN:
			primaryContext = 'connect';
			subContext = ['connectCampaign'];
			hideLegacy = true;
			break;
		case APP_ROUTING_CONSTANTS.CONNECT.TWO_WAY_TEXT:
			primaryContext = 'connect';
			subContext = ['connectTwoWayText'];
			hideLegacy = true;
			break;
		case APP_ROUTING_CONSTANTS.HOME:
			if (featureService.isFeatureOff(FEATURE_FLAGS.MODULES.HOME)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'home']);
			} else if (securityManagerService.sessionData?.displayAgreementsScreen || securityManagerService.sessionData?.displayLegacyAgreementsScreen) {
				// Hiding the legacy iframe here would race with AppComponent.loggedIn() showing the agreements
				// screen in that same iframe, leaving neither surface rendered (REV-9721).
				hideLegacy = false;
			}
			primaryContext = 'home';
			break;
		case APP_ROUTING_CONSTANTS.INVENTORY.PRODUCTS:
			if (featureService.isFeatureOff(FEATURE_FLAGS.MODULES.INVENTORY.PRODUCTS)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'inventory', 'products']);
			}
			primaryContext = 'inventory';
			subContext = ['products'];
			break;
		case APP_ROUTING_CONSTANTS.INVENTORY.SERVICES:
			if (featureService.isFeatureOff(FEATURE_FLAGS.MODULES.INVENTORY.SERVICES)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'inventory', 'services']);
			}
			primaryContext = 'inventory';
			subContext = ['services'];
			break;
		case APP_ROUTING_CONSTANTS.INVENTORY.PACKAGES:
			if (featureService.isFeatureOff(FeatureFlagApplicationKey.ORDER_PACKAGES.value)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'inventory', 'packages']);
			}
			primaryContext = 'inventory';
			subContext = ['packages'];
			break;
		case APP_ROUTING_CONSTANTS.INVENTORY.TRANSFERS:
			if (featureService.isFeatureOff(FEATURE_FLAGS.MODULES.INVENTORY.TRANSFERS)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'inventory', 'transfers']);
			}
			primaryContext = 'inventory';
			subContext = ['transfers'];
			break;
		case APP_ROUTING_CONSTANTS.INVENTORY.RECONCILIATION:
			if (featureService.isFeatureOff(FEATURE_FLAGS.MODULES.INVENTORY.RECONCILIATION)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'inventory', 'reconciliation']);
			}
			primaryContext = 'inventory';
			subContext = ['reconciliation'];
			break;
		case APP_ROUTING_CONSTANTS.INVENTORY.VSP:
			if (featureService.isFeatureOff(FEATURE_FLAGS.MODULES.INVENTORY.VSP)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'inventory', 'vsp']);
			}
			primaryContext = 'inventory';
			subContext = ['vsp'];
			break;
		case APP_ROUTING_CONSTANTS.MESSAGING:
			if (featureService.isFeatureOff(FEATURE_FLAGS.MODULES.MESSAGING)) {
				hideLegacy = false;
				await router.navigate([legacyPath, 'messaging']);
			}
			primaryContext = 'messaging';
			break;
		case APP_ROUTING_CONSTANTS.ORDERS:
		case APP_ROUTING_CONSTANTS.LEGACY_ORDERS:
			primaryContext = 'orders';
			break;
		case APP_ROUTING_CONSTANTS.PATIENTS:
			primaryContext = 'patients';
			break;
		case APP_ROUTING_CONSTANTS.TASKS:
			primaryContext = 'tasks';
			break;
		case APP_ROUTING_CONSTANTS.REV_ENGAGE:
			primaryContext = 'revEngage';
			hideLegacy = true;
			break;
		default:
			hideLegacy = false;
			break;
	}

	if (hideLegacy) {
		legacyNavigationService.hideLegacy(primaryContext, subContext);
	}
	return true;
};
